/**
 * What we CALL an operator on screen.
 *
 * 🔑 A LABEL, NOT A RENAME. `operator` stays in the code, the API paths, the
 * database columns and the RBAC resource, because renaming those reaches 40+
 * radaccess routes, radbilling's direct SQL reads and the RouteClass
 * declarations — a large, risky change for a word nobody outside this file
 * sees. Everything a user reads comes from here.
 *
 * Why "Service Provider": it is what the licence itself says (an ISP is an
 * Internet *Service Provider*), it is equally true of a one-town reseller, and
 * it is already the term used in GST and telecom paperwork — so it reads
 * correctly in a sentence about the supplier on an invoice, which "Partner"
 * does not. "Operator" was dropped because in this market it reads as "cable
 * operator".
 *
 * 🪤 ONE LABEL FOR EVERY TIER, DELIBERATELY. The obvious alternative — a
 * grander name for licensed ISPs and a lesser one for small resellers —
 * reproduces exactly the status problem this change exists to remove. The tier
 * is shown as a neutral badge beside the name instead, so the hierarchy stays
 * visible without ranking anyone's title.
 */
export const PROVIDER = "Service Provider";
export const PROVIDER_LOWER = "service provider";
export const PROVIDER_PLURAL = "Service Providers";

/**
 * The kinds of provider AnkhOS is for.
 *
 * 🔑 POSITIONING, NOT A CLASSIFICATION. This list says who the product serves.
 * It is NOT `OPERATOR_TYPES` (ISP | VNO | OPERATOR), which is a LICENCE class
 * decided by the regulator and stored in `operator_type`. Two lists of provider
 * words one import apart is a real reading hazard, so keep the distinction
 * explicit: nothing here is ever submitted, stored, or used in a decision.
 *
 * 🔑 THIS IS NOW A DECISION INPUT, not only a marketing list. `requiresLicence`
 * drives whether the registration form demands licence-or-upstream, so an edit
 * here changes who can register. It was previously documented as never used in
 * a decision; that stopped being true when the owner established that only
 * internet and VPN providers are licensed.
 *
 * ⚠️ ONE SOURCE ON PURPOSE. The landing page's list and the registration form's
 * picker read the same array. Two lists would drift, and the drift would show up
 * as a provider invited by the front door and refused by the form -- which is
 * exactly the defect this change exists to fix.
 *
 * 🔑 Why the product is not internet-specific: a plan is assembled from the
 * namespace and the catalog, and neither carries anything about connectivity.
 * Metering a VPN seat, a hosting package and a broadband line are the same
 * operation over a different component, which is why one console covers all of
 * them without a per-industry build.
 *
 * ⚠️ "ISP" is not spelled out as a kind here. The segment word is "Internet";
 * the acronym survives ONLY where it is a licence class or a statutory licence
 * name, both of which are the regulator's words rather than ours.
 */
export interface ProviderKind {
  readonly key: string;
  readonly name: string;
  readonly blurb: string;
  /**
   * Whether this kind of provider is licensed in India.
   *
   * 🔑 Owner ruling 2026-09-07: ONLY internet and VPN providers require a
   * licence. Everything else -- infrastructure, cloud, hosting, OTT, SaaS --
   * sells a service that carries no telecom licence at all, so demanding
   * "a licence or an upstream licence holder" from them is asking for a
   * document that does not exist for their business.
   */
  readonly requiresLicence: boolean;
}

export const PROVIDER_KINDS: readonly ProviderKind[] = [
  { key: "network", name: "Network providers", requiresLicence: false,
    blurb: "Wired, wireless and last-mile networks, with resellers beneath them." },
  { key: "internet", name: "Internet providers", requiresLicence: true,
    blurb: "Broadband and leased-line access, licensed or served through a licensed upstream." },
  { key: "infrastructure", name: "Infrastructure providers", requiresLicence: false,
    blurb: "Fibre, towers, ducts and dark capacity billed to the operators that use them." },
  { key: "cloud", name: "Cloud providers", requiresLicence: false,
    blurb: "Compute, storage and managed platforms sold by subscription or by usage." },
  { key: "hosting", name: "Hosting providers", requiresLicence: false,
    blurb: "Servers, colocation, domains and mail, with per-package renewals." },
  { key: "vpn", name: "VPN providers", requiresLicence: true,
    blurb: "Per-seat and per-tunnel plans, metered the same way a data plan is." },
  { key: "ott", name: "OTT providers", requiresLicence: false,
    blurb: "Streaming and application services, bundled on their own or with access." },
  { key: "saas", name: "SaaS providers", requiresLicence: false,
    blurb: "Software sold per seat or per tier, renewed on a cycle like any other plan." },
  // 🔑 THE LIST IS NOT A GATE, and this entry is what says so. The seven above
  // are examples; the actual qualifier is the last one -- a provider that bills
  // subscribers on recurring plans. Without it a reader whose business is not
  // named assumes the product is not for them, which is the opposite of the
  // point. Keep it last: it generalises the list, so it only reads correctly
  // after the concrete cases.
  { key: "other", name: "…and others like them", requiresLicence: false,
    blurb: "Any provider that bills subscribers on a recurring cycle — several plans, several " +
           "price lists, renewals and upgrades that have to reconcile." },
];

/**
 * The three tiers, shown as a badge rather than folded into the name.
 *
 * They are not decoration: the tier decides who may invoice pure internet
 * service, so an operator who cannot see their own tier cannot see why a
 * capability is unavailable to them.
 */
export const TIER_LABEL: Record<string, string> = {
  ISP: "ISP",
  VNO: "VNO",
  // Was "Local" until 2026-10-09: the owner found it confusing next to Network
  // operator, because it named WHERE the operator works, not what it may sell.
  OPERATOR: "Reseller (no licence)",
  // Set only from the kind a parent picks under "+ Add New Provider"
  // (owner 2026-09-19); never offered at sign-up.
  PIN: "PIN operator",
  FRANCHISE: "Franchise",
  // Neither invoices a subscriber for internet service.
  CLOUD_TENANT: "Cloud tenant",
  NETWORK_OPERATOR: "Network operator",
};

/** Human tier name for a badge. Falls back to the raw value rather than hiding it. */
export function tierLabel(operatorType: string): string {
  return TIER_LABEL[operatorType] ?? operatorType;
}

/**
 * One line saying what each tier IS, shown under the tier selector as soon as
 * one is chosen (owner 2026-10-09, wording approved as written).
 *
 * 🔑 Each line answers the two questions that decide approval: whose name the
 * internet invoice carries, and whether an upstream must be named. A tier
 * picked without knowing that is how an unlicensed reseller ends up asking to
 * be approved with no upstream.
 */
export const TIER_EXPLAINED: Record<string, string> = {
  ISP: "Holds its own ISP licence (A/B/C). Sells internet in its own name. No upstream.",
  VNO: "Holds a Virtual Network Operator licence. Buys bandwidth wholesale, sells in its own name. No upstream.",
  OPERATOR:
    "Sells internet to subscribers on behalf of a licensed ISP or VNO (its upstream). Internet invoices go " +
    "out in the upstream's name, so an upstream must be named before approval.",
  NETWORK_OPERATOR:
    "Runs the network for a campus, company or society that buys its own bandwidth. Never invoices " +
    "internet; charges for managing the network.",
  CLOUD_TENANT:
    "Runs its own applications on the Trinetra platform. Billed for resources used. No subscribers, no internet.",
};

/** The explanation for a tier, or "" for one that has none (PIN, FRANCHISE). */
export function tierExplained(operatorType: string): string {
  return TIER_EXPLAINED[operatorType] ?? "";
}

/** The kinds that carry a telecom/ISP licence. Everything else does not. */
export function kindRequiresLicence(key: string): boolean {
  return PROVIDER_KINDS.find((k) => k.key === key)?.requiresLicence ?? false;
}

/**
 * True if ANY selected service is licensed.
 *
 * 🔑 ANY, not all. An ISP that also sells hosting and OTT still needs its
 * internet licence — the unlicensed lines do not dilute the licensed one. The
 * mirror error would be to ask a pure SaaS provider for a telecom licence.
 */
export function anyKindRequiresLicence(keys: readonly string[]): boolean {
  return keys.some(kindRequiresLicence);
}

/** The kinds an applicant can pick. Excludes the marketing catch-all. */
export const SELECTABLE_PROVIDER_KINDS = PROVIDER_KINDS.filter((k) => k.key !== "other");
